import { Ref } from 'vue';
import { useUserStore } from '@/store';
import { storeToRefs } from 'pinia';
import {
  accAdd,
  accDiv,
  accMul,
  accSub,
  checkAuthority,
  getFabricTypeName,
  getLossRateByOrderNumber,
  getLossRateRange,
  handleFile,
} from '@/utils/common';
import globalContants from '@/constants/globalContants';
import { cloneDeep, isNumber, isUndefined, pick } from 'lodash';

const userStore = useUserStore();
const { materialDecimal } = storeToRefs(userStore);
// 展开firstProduceOrderMaterials
export const handleFirstProduceOrderMaterials = (list: Recordable[]) => {
  const data: Recordable[] = [];
  list.forEach((row: any) => {
    const { firstProduceOrderMaterials = [], ...tempRow } = row;
    if (firstProduceOrderMaterials.length) {
      firstProduceOrderMaterials.forEach((item: any) => {
        // 只拿需要的 防止展开后覆盖了原有同名字段
        const {
          materialSkuId,
          materialName,
          materialSn,
          materialColor,
          materialSkuPropertyList,
          supplierName,
          materialQunce,
          purchasePrice,
          price,
          materialFabricWidth,
          materialComposition,
          remark,
        } = item;
        data.push({
          ...tempRow,
          rowKey: `${tempRow.id}_${materialSkuId}`, // 表格rowConfig.keyField使用的唯一键
          materialName,
          materialSn,
          materialColor,
          materialSkuPropertyList,
          supplierName,
          materialQunce,
          purchasePrice,
          price,
          materialFabricWidth,
          materialComposition,
          materialRemark: remark,
        });
      });
    } else {
      data.push(row);
    }
  });
  return data;
};

// 处理接口返回的尺寸指示
export const handleSizeIndicator = (
  goodsPartList: Recordable[],
  sizeDisplay: string,
  mode: 'goods' | 'design' | 'designOrder' = 'goods'
) => {
  const sizeIndicateData: Recordable[] = [];
  const partSizeLenData: Recordable = {};
  const getPartSizeLengthListField = () => {
    if (mode === 'design') return 'designPartSizeLengths';
    return mode === 'designOrder'
      ? 'partSizeLengthList'
      : 'goodsPartSizeLengthList';
  };
  const partSizeLengthListField = getPartSizeLengthListField();
  goodsPartList.forEach((part: any) => {
    const {
      [partSizeLengthListField]: goodsPartSizeLengthList = [],
      ...partParams
    } = part;
    const temp: Recordable = { ...partParams };
    temp.gradingDifference =
      temp.gradingDifference || temp.gradingDifference === 0
        ? temp.gradingDifference.toString()
        : '';
    temp.gradingDifferenceAfterWash =
      temp.gradingDifferenceAfterWash || temp.gradingDifferenceAfterWash === 0
        ? temp.gradingDifferenceAfterWash.toString()
        : '';
    if (sizeDisplay === '1') {
      temp.errorRangeStr =
        temp.errorRange || temp.errorRange === 0
          ? temp.errorRange.toString()
          : '';
    }
    goodsPartSizeLengthList.forEach((size: any) => {
      const { sizeId, length, lengthStr, lengthAfterWash, lengthStrAfterWash } =
        size;
      temp[sizeId] = sizeDisplay === '1' ? length?.toString() : lengthStr;
      temp[`${sizeId}-afterWash`] =
        sizeDisplay === '1' ? lengthAfterWash?.toString() : lengthStrAfterWash;
    });
    partSizeLenData[partParams.id] = goodsPartSizeLengthList;
    sizeIndicateData.push(temp);
  });
  return { sizeIndicateData, partSizeLenData };
};

export const getSizeIndicateData = (
  sizeIndicate: Recordable[],
  sizeList: Recordable[],
  idVal: any,
  idField: 'goodsId' | 'designId' | 'designOrderId' = 'goodsId'
) => {
  const goodsPartList: Recordable[] = [];
  const getPartSizeLengthListField = () => {
    if (idField === 'designId') return 'designPartSizeLengths';
    return idField === 'designOrderId'
      ? 'partSizeLengthList'
      : 'goodsPartSizeLengthList';
  };
  const partSizeLengthListField = getPartSizeLengthListField();
  // 判断是否是数字
  const reg = /^[0-9]+\.?[0-9]*$/;
  const sizeIndicateTemp = sizeIndicate.filter((item: any) => {
    // 过滤掉空数据
    return item.name !== '';
  });
  sizeIndicateTemp.forEach((indicate: any) => {
    const {
      name: indicateName,
      measureMethod,
      errorRangeStr = '',
      gradingDifference,
      gradingDifferenceAfterWash,
      sampleLengthStr,
      id,
      attachments,
      ...sizeParams
    } = indicate;
    // gradingDifference、errorRange为input-number组件，只能限制输入数字
    const temp: any = {
      name: indicateName,
      measureMethod,
      errorRangeStr,
      gradingDifference: reg.test((gradingDifference ?? '').toString())
        ? gradingDifference
        : '',
      gradingDifferenceAfterWash: reg.test(
        (gradingDifferenceAfterWash ?? '').toString()
      )
        ? gradingDifferenceAfterWash
        : '',
      sampleLengthStr,
      attachments: handleFile(attachments),
      [partSizeLengthListField]: [],
    };
    // 尺码列
    sizeList?.forEach((size: any) => {
      const { id: sizeId, name: sizeName } = size;
      const sizeTemp: Recordable = {
        sizeId,
        sizeName,
        isDisable: +!size.able,
      };
      let isAddSize = false;
      const sizeKey = sizeId.toString();
      if (
        sizeParams[sizeKey] &&
        sizeParams[sizeKey]?.toString()?.trim() &&
        sizeParams[sizeKey] !== 0
      ) {
        Object.assign(sizeTemp, {
          lengthStr: sizeParams[sizeKey],
        });
        isAddSize = true;
      }
      const afterWashSizeKey = `${sizeId}-afterWash`;
      if (
        sizeParams[afterWashSizeKey] &&
        sizeParams[afterWashSizeKey]?.toString()?.trim() &&
        sizeParams[afterWashSizeKey] !== 0
      ) {
        Object.assign(sizeTemp, {
          lengthStrAfterWash: sizeParams[afterWashSizeKey],
        });
        isAddSize = true;
      }
      if (isAddSize) {
        temp[partSizeLengthListField].push(sizeTemp);
      }
    });

    if (idVal || idVal === 0) {
      temp[idField] = idVal;
    }
    if (id || id === 0) {
      temp.id = id;
    }
    goodsPartList.push(temp);
  });
  return goodsPartList;
};

// 过滤尺寸指示空数据
export const filterEmptySizeIndicate = (sizeIndicate: Recordable[]) => {
  return sizeIndicate.filter((item: any) => {
    return (
      item.name !== '' ||
      item.measureMethod !== '' ||
      item.errorRange !== '' ||
      item.sampleLengthStr !== '' ||
      (item.gradingDifference && item.gradingDifference === 0) ||
      (item.gradingDifferenceAfterWash && item.gradingDifferenceAfterWash === 0)
    );
  });
};

// 格式化bom的单元格默认值
export const formatterBomDefaultVal = ({ row, cellValue }: any) => {
  if (row.blankLine) return '';
  return cellValue ?? '-';
};

// 格式化bom基础单价
export const formatterPrice = ({ row }: any) => {
  const { blankLine, priceRange, price } = row;
  if (blankLine) return '';
  if (priceRange) return priceRange;
  return `${globalContants.CURRENCYSYMBOL}${price}`;
};

// 格式化bom采购单价
export const formatterPurchasePrice = ({ row }: any) => {
  const { blankLine, purchasePriceRange, purchasePrice } = row;
  if (blankLine) return '';
  if (purchasePriceRange) return purchasePriceRange;
  return `${globalContants.CURRENCYSYMBOL}${purchasePrice}`;
};
// 格式化bom采购员
export const formatterPurchaseUsers = ({ row }: any) => {
  const { blankLine, purchaseUserItems } = row;
  if (blankLine) return '';
  return purchaseUserItems
    ?.map((item: any) => item.purchaseUserName ?? item.name)
    .join('、');
};

// 格式化bom损耗率
export const formatterMaterialLossRate = ({ row }: any) => {
  const {
    blankLine,
    lossRateStairEnable,
    lossRateStairItems,
    materialLossRate,
  } = row;
  if (blankLine) return '';
  if (lossRateStairEnable) return getLossRateRange(lossRateStairItems);
  return materialLossRate;
};

// 获取bom物料颜色规格下拉框
export const getSelectName = (
  value: any,
  key: string,
  returnField: string,
  list: Recordable[]
) => {
  return list?.find((e: Recordable) => e[key] === value)?.[returnField] ?? '-';
};

// 格式化bom成衣颜色
export const formatterColor = ({ row }: any, colorId: number | string) => {
  if (row.blankLine) return '';
  if (!row.selectColorList?.length) return '-';
  return getSelectName(
    row[`goodsColorId-${colorId}`],
    'colorId',
    'colorNameAndSupplierColor',
    row.selectColorList
  );
};

// 格式化bom物料规格
export const formatterProperty = ({ row }: any, sizeId: number | string) => {
  if (row.blankLine) return '';
  if (!row.selectPropertyList?.length) return '-';
  return getSelectName(
    row[`goodsSizeId-property-${sizeId}`],
    'propertyIds',
    'propertyIdsName',
    row.selectPropertyList
  );
};

// 格式化bom模版单品名称
export const formatterTempSkuName = ({ row }: any) => {
  if (row.blankLine) return '';
  if (!row.materialSkuDTOList?.length) return '-';
  return getSelectName(row.tempSkuId, 'id', 'skuName', row.materialSkuDTOList);
};

// 格式化bom平均规格
export const formatterPropertyAvg = ({ row }: any) => {
  if (row.blankLine) return '';
  if (!row.selectPropertyList?.length) return '-';
  return getSelectName(
    row.propertyAvg,
    'propertyIds',
    'propertyIdsName',
    row.selectPropertyList
  );
};

// 根据用量计算含损耗用量
export const getBomIncludingLossNumber: any = (
  material: Recordable,
  number: number
) => {
  const { materialLossRate = 0, shrinkRate = 0 } = material;
  return accDiv(
    accMul(number || 0, accAdd(1, materialLossRate / 100)),
    accSub(1, shrinkRate / 100),
    4
  );
};

// 格式化含损耗用量
export const formatterIncludingLossNumber = ({ row, column }: any) => {
  if (row.blankLine) return '';
  const { field } = column;
  const sizeIdStr = field?.split('-')?.pop();
  if (sizeIdStr) {
    if (sizeIdStr === 'number')
      return isNumber(row.number)
        ? getBomIncludingLossNumber(row, row.number)
        : '-';
    const number = row[`goodsSizeId-number-${sizeIdStr}`];
    return isNumber(number) ? getBomIncludingLossNumber(row, number) : '-';
  }
  return '-';
};

// 获取年份列表
export const getYearList = (notAllYears = false) => {
  const date = new Date();
  const maxYear = date.getFullYear() + 2;
  const minYear = 2010;
  const yearList = [];
  if (!notAllYears) {
    yearList.push({
      name: '全部年份',
      value: 0,
    });
  }
  for (let i = maxYear; i >= minYear; i -= 1) {
    const minObj = {
      name: `${i}年上`,
      value: i,
    };
    const maxObj = {
      name: `${i}年下`,
      value: i + 0.5,
    };
    yearList.push(maxObj, minObj);
  }
  return yearList;
};

// 用于IE核价报表
export function handleMaterialSku(list: Recordable[] = []) {
  const res: Recordable[] = [];
  const skuMap: Recordable<
    [designSkuSizeMap: string[], length: number, num: number]
  > = {}; // 用来存储物料单品的总用量和成衣数量，便于计算单件用量
  const designSkuInfo: Recordable = {
    skuMap: {},
    skuNumber: 0,
    // 面料汇总信息
    fabricSummaryInfo: {
      totalCost: 0,
      includingLossNumber: 0,
    },
    accessorySummaryInfo: {
      totalCost: 0,
    },
  };
  cloneDeep(list).forEach((item) => {
    if (item.number) {
      const {
        materialSkuId,
        designSkuColorId,
        designSkuSizeId,
        materialSkuProperties,
        number,
        materialPart = '',
        materialTypeName = '',
      } = item;
      const materialSpec = materialSkuProperties
        .map((e: any) => e.propertyValue)
        .join('/'); // 物料规格
      const sku = res.find(
        (e) =>
          e.materialSkuId === materialSkuId && e.materialSpec === materialSpec
      );
      const skuField = `${materialSkuId}-${materialSpec}`;
      const designSkuKey = `${designSkuColorId}-${designSkuSizeId}`;
      const includingLossNumber = getBomIncludingLossNumber(item, number); // 含损耗用量
      if (sku) {
        skuMap[skuField][2] += includingLossNumber;
        if (!skuMap[skuField][0].includes(designSkuKey)) {
          skuMap[skuField][0].push(designSkuKey);
          skuMap[skuField][1] += 1;
        }
        if (!sku.materialParts.includes(materialPart)) {
          sku.materialParts.push(materialPart);
        }
      } else {
        item.materialSpec = materialSpec;
        skuMap[skuField] = [[designSkuKey], 1, includingLossNumber];
        res.push(item);
        item.materialParts = [materialPart];
      }
      if (!designSkuInfo.skuMap[designSkuKey]) {
        designSkuInfo.skuMap[designSkuKey] = true;
        designSkuInfo.skuNumber += 1;
      }
      if (materialTypeName === '面料') {
        designSkuInfo.fabricSummaryInfo.totalCost =
          accAdd(
            designSkuInfo.fabricSummaryInfo.totalCost || 0,
            accMul(item.price, includingLossNumber)
          ) || 0;
        designSkuInfo.fabricSummaryInfo.includingLossNumber = accAdd(
          designSkuInfo.fabricSummaryInfo.includingLossNumber || 0,
          includingLossNumber
        );
      } else {
        designSkuInfo.accessorySummaryInfo.totalCost = accAdd(
          designSkuInfo.accessorySummaryInfo.totalCost || 0,
          accMul(item.price, includingLossNumber)
        );
      }
    }
  });
  res.forEach((item) => {
    const { materialSkuId, materialSpec } = item;
    const [, length, num] = skuMap[`${materialSkuId}-${materialSpec}`];
    item.number = accDiv(num, length, 4) ?? '-';
    item.skuNumber = designSkuInfo.skuNumber;
    if (item.materialTypeName === '面料') {
      item.fabricSummaryInfo = designSkuInfo.fabricSummaryInfo;
    } else {
      item.accessorySummaryInfo = designSkuInfo.accessorySummaryInfo;
    }
  });
  return res;
}

/**
 * 用于查找当前点击的元素是否在指定类名下
 * @param target 点击的元素
 * @returns
 */
export const isClickEditorElement = (target: HTMLElement) => {
  return checkParentNodeExistClassName(
    target,
    globalContants.SELECT_POPUP_TRIGGER
  );
  // while (target) {
  //   // 约定的类名
  //   if (
  //     target.classList &&
  //     target.classList.contains(globalContants.SELECT_POPUP_TRIGGER)
  //   ) {
  //     return true;
  //   }
  //   target = target.parentNode as HTMLElement;
  // }
  // return false;
};
/**
 * 用于查找当前元素是否在指定类名下
 * @param target 点击的元素
 * @param className 寻找的类型
 * @param count 寻找次数
 * @returns
 */
export const checkParentNodeExistClassName = (
  target: HTMLElement,
  className: string,
  count?: number
) => {
  const checkCount = !isUndefined(count);
  let num = isUndefined(count) ? 0 : -count;
  while (target) {
    if (checkCount && num === 0) {
      return false;
    }
    // 约定的类名
    if (target.classList && target.classList.contains(className)) {
      return true;
    }
    target = target.parentNode as HTMLElement;
    num += 1;
  }
  return false;
};

export const goodsBomDefaultCol = (
  useComposition: Ref<boolean>,
  usePart: Ref<boolean>,
  usePostProcess: Ref<boolean>,
  mode?: string
) => {
  return [
    {
      field: 'imgUrl',
      title: '图片',
      fixed: 'left',
    },
    {
      field: 'name',
      title: '物料名称',
      fixed: 'left',
      showTooltip: true,
      edit: true,
      width: 120,
    },
    {
      field: 'sn',
      title: '物料货号',
      fixed: 'left',
      width: 80,
    },
    {
      hide: !checkAuthority('supplierInfoSupplierGetDataPermission'),
      field: 'supplierName',
      title: '供应商',
      edit: true,
    },
    {
      field: 'materialSupplierSn',
      title: '供应商货号',
      fixed: 'left',
      width: 100,
    },
    {
      hide: !checkAuthority('supplierInfoSupplierGetDataPermission'),
      field: 'supplierAddressText',
      title: '地址',
      width: 120,
    },
    {
      hide: !checkAuthority('supplierInfoSupplierGetDataPermission'),
      field: 'supplierCellPhone',
      title: '联系电话',
      width: 120,
    },
    {
      field: 'materialTypeName',
      title: '类型',
      width: 80,
      edit: true,
    },
    {
      field: 'fabricType',
      title: '面料属性',
      width: 60,
      formatter: ({ row, cellValue }: any) => {
        if (row.blankLine) return '';
        return getFabricTypeName(cellValue) || '-';
      },
    },
    {
      field: 'unitName',
      title: '基础单位',
      width: 80,
    },
    {
      field: 'price',
      title: '基础单价',
      hide: !checkAuthority('materialCostPriceGet'),
      align: 'right',
      width: 120,
      edit: true,
      formatter: formatterPrice,
    },
    {
      field: 'convertRate',
      title: '转换系数',
      align: 'right',
      tips: '影响不同单位的数值转换，可以理解为“1个采购单位换算多少基础单位”。\n例如梭织面料，采购单位有可能是“码”，入库的库存数需要转换基础单位“米”，1码=0.9144米，则转换系数为0.9144，表示买1码布，入库0.9144米',
      width: 90,
      edit: true,
    },
    {
      field: 'purchaseUnitName',
      title: '采购单位',
      width: 80,
    },
    {
      field: 'purchasePrice',
      title: '采购单价',
      hide: !checkAuthority('materialCostPriceGet'),
      align: 'right',
      width: 120,
      edit: true,
      formatter: formatterPurchasePrice,
    },
    {
      field: 'purchaseUserItems',
      title: '采购员',
      width: 120,
      edit: true,
      showTooltip: true,
      formatter: formatterPurchaseUsers,
    },
    {
      field: 'materialMeterOutput',
      title: '出米数(m/kg)',
      align: 'right',
      width: 90,
    },
    {
      field: 'paperBucket',
      title: '纸筒(kg)',
      align: 'right',
      width: 70,
    },
    {
      field: 'emptyGap',
      title: '空差(kg)',
      align: 'right',
      width: 70,
    },
    {
      field: 'materialNetWeight',
      title: '净重(kg)',
      align: 'right',
      width: 70,
    },
    {
      field: 'materialFabricWidth',
      title: '幅宽(cm)',
      align: 'right',
      width: 70,
    },
    {
      field: 'materialGramWeight',
      title: '克重(g/㎡)',
      align: 'right',
      width: 70,
    },
    {
      field: 'materialQunce',
      title: '安数',
      tips: '衡量牛仔布类型面料厚度的单位，会自动换算成克重，克重（g/m²）=安数*33.9',
      align: 'right',
      width: 70,
      defaultHidden: true,
    },
    {
      field: 'materialLossRate',
      title: '损耗率(%)',
      width: 100,
      align: 'right',
      tips: '商品的BOM资料会默认带入物料的损耗值（可以修改），商品的物料损耗值会影响生产单的预计用量；\n预计总用量=数量*单件用量*（1+损耗率）/（1-缩水率）',
      edit: true,
    },
    {
      title: '缩水率(%)',
      field: 'shrinkRate',
      width: 90,
      align: 'right',
      tips: '缩水率会影响采购需求数计算，公式：采购需求=用量*（1+损耗率）/（1-缩水率）',
      defaultHidden: true,
      edit: true,
    },
    {
      field: 'materialComposition',
      title: '成分',
      defaultHidden: !useComposition.value,
    },
    {
      field: 'materialPart',
      title: '使用部位',
      defaultHidden: !usePart.value,
      width: 120,
      maxLength: 30,
      showTooltip: true,
      edit: true,
    },
    {
      field: 'materialPostProcess',
      title: '后处理',
      defaultHidden: !usePostProcess.value,
      showTooltip: true,
      width: 120,
      maxLength: 200,
      edit: true,
    },
    {
      field: 'remark',
      title: '物料备注',
      width: 120,
      showTooltip: true,
      defaultHidden: true,
    },
    {
      field: 'materialCategoryName',
      title: '物料分类',
      defaultHidden: true,
    },
    {
      field: 'colorGroup',
      title: mode === 'template' ? '物料单品' : '成衣配色',
      edit: true,
    },
    {
      field: 'sizeGroup',
      title: '用量',
      edit: true,
    },
  ];
};

// 判断单品数组中物料损耗率是否一致
const checkMaterialLossRateIsUnique = (checkArr: Recordable[]) => {
  const [
    {
      lossRateStairEnable: firstLossRateStairEnable = 0,
      lossRateStairType: firstLossRateStairType = 2,
      materialLossRate: firstMaterialLossRate,
      lossRateStairItems: firstMaterialLossRateStairs = [],
    },
  ] = checkArr;
  return checkArr.every((sku: Recordable) => {
    const {
      lossRateStairEnable = 0,
      lossRateStairType = 2,
      materialLossRate,
      lossRateStairItems = [],
    } = sku;
    if (firstLossRateStairEnable !== lossRateStairEnable) return false;
    if (!lossRateStairEnable) {
      // 没开阶梯损耗率
      if (firstMaterialLossRate === materialLossRate) return true;
      return false;
    }
    // 开了阶梯损耗率
    if (firstLossRateStairType !== lossRateStairType) return false;
    if (firstMaterialLossRateStairs.length !== lossRateStairItems.length)
      return false;
    return firstMaterialLossRateStairs.every(
      (item: Recordable, index: number) =>
        item.orderNumber === lossRateStairItems[index].orderNumber &&
        item.lossRate === lossRateStairItems[index].lossRate
    );
  });
};
// 判断单品的【单品不一致字段】(后处理-materialPostProcess、使用部位-materialPart、物料损耗率-materialLossRate、缩水率-shrinkRate)是否一致
export const checkSkuIsUnique = (checkArr: Recordable[]) => {
  const [
    {
      materialPostProcess: firstMaterialPostProcess,
      materialPart: firstMaterialPart,
      shrinkRate: firstShrinkRate,
    },
  ] = checkArr;
  return (
    checkArr.every((sku: Recordable) => {
      const { materialPostProcess, materialPart, shrinkRate } = sku;
      return (
        firstMaterialPostProcess === materialPostProcess && // sku -> 后处理可以不一致
        firstMaterialPart === materialPart && // sku -> 使用部位可以不一致
        firstShrinkRate === shrinkRate // sku -> 缩水率可以不一致
      );
    }) && checkMaterialLossRateIsUnique(checkArr) // sku -> 损耗率可以不一致
  );
};

// 获取每个物料单品对应涉及成衣的下单数
const getMtSkuOrderNumberSumMap = (
  orderGoodsList: Recordable[],
  goodsMaterialList: Recordable[]
) => {
  const goodsSkuIdMapNumber = new Map();
  orderGoodsList.forEach((sku: any) => {
    const { skuId, id, number = 0 } = sku;
    goodsSkuIdMapNumber.set(skuId ?? id, number);
  });
  return goodsMaterialList.reduce((prev, cur) => {
    const { materialSkuId, goodsSkuId, number } = cur;
    if (!prev[materialSkuId]) {
      prev[materialSkuId] = {
        sumNumber: 0,
        skuIds: [],
      };
    }
    if (!prev[materialSkuId].skuIds.includes(goodsSkuId) && number) {
      // 只记有用量的物料
      // 获取skuId 生产的数量
      const proNumber = goodsSkuIdMapNumber.get(goodsSkuId);
      prev[materialSkuId].sumNumber = accAdd(
        prev[materialSkuId].sumNumber,
        proNumber || 0
      );
      prev[materialSkuId].skuIds.push(goodsSkuId);
    }
    return prev;
  }, {});
};

// 获取生产单需要的materialList（可用于拆单、修改下单数）
export const handleMtList = (
  materialList: Recordable[],
  goodsMaterialList: Recordable[],
  orderGoodsList: Recordable[]
) => {
  const orderNumberSum = orderGoodsList.reduce(
    (sum, item) => accAdd(sum, item.number || 0) || 0,
    0
  );
  const goodsSkuIds = orderGoodsList.map((item) => item.skuId);
  const mtSkuIds = materialList.map((item) => item.materialSkuId);
  const filterBom = goodsMaterialList.filter(
    (item) =>
      mtSkuIds.includes(item.materialSkuId) &&
      goodsSkuIds.includes(item.goodsSkuId)
  );
  const mtSkuOrderNumberSumMap = getMtSkuOrderNumberSumMap(
    orderGoodsList,
    filterBom
  );
  const goodsSkuIdMapNumber = new Map();
  orderGoodsList.forEach((sku: any) => {
    const { skuId, id, number = 0 } = sku;
    goodsSkuIdMapNumber.set(skuId ?? id, number);
  });
  const mtSkuIdToOrderNumberMap = filterBom.reduce(
    (prev: Recordable, cur: Recordable) => {
      const {
        materialSkuId,
        goodsSkuId,
        number,
        materialLossRate,
        shrinkRate,
      } = cur;
      if (!prev[materialSkuId]) {
        prev[materialSkuId] = 0;
      }
      const proNumber = goodsSkuIdMapNumber.get(goodsSkuId);
      const orderNumberSumByMaterialSkuId =
        mtSkuOrderNumberSumMap[materialSkuId]?.sumNumber ?? 0;
      const orderNumber = accDiv(
        accMul(
          accMul(proNumber, number),
          accAdd(
            1,
            getLossRateByOrderNumber(
              cur,
              materialLossRate,
              orderNumberSum,
              orderNumberSumByMaterialSkuId
            ) / 100
          )
        ),
        accSub(1, (shrinkRate ?? 0) / 100),
        4
      );
      prev[materialSkuId] = accAdd(prev[materialSkuId], orderNumber);
      return prev;
    },
    {}
  );

  return materialList.map((item: Recordable) => {
    const number = mtSkuIdToOrderNumberMap[item.materialSkuId] ?? 0;
    const amount = accMul(number, item.price || 0) ?? 0;
    return {
      ...pick(item, [
        'materialId',
        'materialSkuId',
        'convertRate',
        'price',
        'purchasePrice',
        'supplierId',
        'remark',
      ]),
      amount,
      number,
    };
  });
};

/**
 * 根据商品和物料关联关系的单品列表计算单件用量（spu | sku）
 * @param goodsMaterialList 物料单品列表
 * @param field materialId | materialSkuId 计算不同维度的单件用量
 * @returns
 */
export const getSingleQuantity = (
  goodsMaterialList: Recordable[],
  field = 'materialId'
) => {
  const data: Recordable[] = [];
  const materialObj: Recordable = {};
  // 有物料用量的商品单品数
  const goodsSkuObj: Recordable = {
    sku: {},
    number: 0,
    totalCost: 0,
    includingLossTotalCost: 0,
  };
  goodsMaterialList?.forEach((sku: Recordable) => {
    const {
      price = 0,
      number = 0,
      goodsColorId,
      goodsSizeId,
      imgUrl,
      sn,
      unitName,
      name,
      materialTypeName,
    } = sku;
    if (!number) return;
    const fieldVal = sku[field];
    const includingLossNumber = getBomIncludingLossNumber(sku, number); // 含损耗用量
    if (materialObj[fieldVal]) {
      materialObj[fieldVal].number = accAdd(
        materialObj[fieldVal].number,
        number,
        4
      );
      materialObj[fieldVal].cost = accAdd(
        materialObj[fieldVal].cost,
        accMul(number, price, 4),
        4
      );
      materialObj[fieldVal].includingLossNumber = accAdd(
        materialObj[fieldVal].includingLossNumber,
        includingLossNumber,
        4
      );
      materialObj[fieldVal].includingLossCost = accAdd(
        materialObj[fieldVal].includingLossCost,
        accMul(includingLossNumber, price, 4),
        4
      );
    } else {
      materialObj[fieldVal] = {
        number,
        cost: accMul(number, price, 4),
        includingLossNumber,
        includingLossCost: accMul(includingLossNumber, price, 4),
        sku: {},
        skuNumber: 0,
      };
      data.push({
        [field]: fieldVal,
        imgUrl,
        sn,
        unitName,
        name,
        materialTypeName,
      });
    }
    if (!materialObj[fieldVal]?.sku?.[`${goodsColorId}-${goodsSizeId}`]) {
      // 累加单品数
      materialObj[fieldVal].sku[`${goodsColorId}-${goodsSizeId}`] = true;
      materialObj[fieldVal].skuNumber += 1;
    }
    if (!goodsSkuObj.sku?.[`${goodsColorId}-${goodsSizeId}`]) {
      // 累加单品数
      goodsSkuObj.sku[`${goodsColorId}-${goodsSizeId}`] = true;
      goodsSkuObj.number += 1;
    }
  });
  data.forEach((material: Recordable) => {
    const item = materialObj[material[field]];
    if (!item) return;
    const { skuNumber, cost, includingLossCost } = item;
    // 单件用量 = 物料规格1所有成衣用量+...+物料规格n所有成衣用量）/该物料涉及成衣数
    material.singleQuantity = skuNumber
      ? accDiv(item.number, skuNumber, 4) || 0
      : 0;
    material.singleIncludingLossQuantity = skuNumber
      ? accDiv(item.includingLossNumber, skuNumber, 4) || 0
      : 0;
    material.number = item.number; // 物料总用量
    material.includingLossNumber = item.includingLossNumber; // 含损总用量
    material.costTotal = cost; // 物料总成本
    material.avgCost = accDiv(cost, skuNumber, 4); // 单件成本 = 该物料所有成本 / 该物料涉及成衣数
    // 平均单价 = 单件成本 ÷ 单件用量
    material.avgPrice = accDiv(material.avgCost, material.singleQuantity, 4);
    // 所有物料总成本
    goodsSkuObj.totalCost = accAdd(goodsSkuObj.totalCost, item.cost, 4) || 0;
    material.avgIncludingLossCost = accDiv(includingLossCost, skuNumber, 4); // 含损单件成本 = 该物料所有含损成本 / 该物料涉及成衣数
    // 平均单价 = 单件成本 ÷ 单件用量
    material.avgIncludingLossPrice = accDiv(
      material.avgIncludingLossCost,
      material.singleIncludingLossQuantity,
      4
    );
    material.includingLossCost = includingLossCost; // 含损总用量
    // 所有物料总成本
    goodsSkuObj.includingLossTotalCost =
      accAdd(goodsSkuObj.includingLossTotalCost, includingLossCost, 4) || 0;
  });
  return { list: data, materialObj, goodsSkuObj };
};

export default { handleFirstProduceOrderMaterials };
